import { Box, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { initializeRetrofits } from '@/features/isfp-upload/utils/initializeAssessmentData'
import { useCustomAssessmentInput, useSubmitCustomAssessmentInput } from '@/shared/api'
import Button from '@/shared/components/Button'
import LoadingIndicator from '@/shared/components/LoadingIndicator'
import TextButton from '@/shared/components/TextButton'
import type { CustomAssessmentInput } from '@/shared/types/custom-assessment-input.types'

import type { useISFPFormController } from '../../hooks/useISFPFormController'
import {
  AssessmentDataValidationError,
  validateAssessmentData,
} from '../../utils/validateAssessmentData'

import CostAdjustmentContent from './CostAdjustmentContent'
import FundingContent from './FundingContent'

interface OptionalFieldsProps {
  formController: ReturnType<typeof useISFPFormController>
  onComplete?: () => void
}

type OptionalFieldsState = 'cost-adjustment' | 'funding'

const OptionalFields = ({ formController, onComplete }: OptionalFieldsProps) => {
  const { buildingUuid } = useParams<{ buildingUuid: string }>()
  const [currentState, setCurrentState] = useState<OptionalFieldsState>('cost-adjustment')
  const [showBottomGradient, setShowBottomGradient] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [assessmentData, setAssessmentData] = useState<CustomAssessmentInput | null>(null)

  // Fetch optional fields defaults
  const {
    data: customAssessmentInput,
    isLoading,
    error,
    refetch,
  } = useCustomAssessmentInput(buildingUuid)

  // Submit custom assessment input mutation
  const { mutate: submitCustomAssessmentInput, isPending: isSubmitting } =
    useSubmitCustomAssessmentInput({
      onSuccess: () => {
        toast.success('Optionale Felder erfolgreich gespeichert!', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
        })
        if (onComplete) {
          onComplete()
        }
      },
      onError: error => {
        toast.error(`Fehler beim Speichern der optionalen Felder: ${error.message || error}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
        })
      },
    })

  // Store assessment data when fetched
  useEffect(() => {
    if (customAssessmentInput && !assessmentData) {
      setAssessmentData({
        buildingId: customAssessmentInput.buildingId,
        subsidyInput: customAssessmentInput.subsidyInput,
        energyPrices: customAssessmentInput.energyPrices,
        retrofits: initializeRetrofits(customAssessmentInput.retrofits),
      })
    }
  }, [customAssessmentInput, assessmentData])

  // Handle scroll detection for bottom gradient
  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current
      if (!container) return

      // Check if there's more content to scroll down
      const hasMoreContent =
        container.scrollHeight - container.scrollTop - container.clientHeight > 1

      setShowBottomGradient(hasMoreContent)
    }

    const container = scrollContainerRef.current
    if (!container) {
      return undefined
    }

    // Check initially
    handleScroll()

    // Add scroll listener
    container.addEventListener('scroll', handleScroll)
    // Add resize listener to recheck when window resizes
    window.addEventListener('resize', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [currentState, isLoading, error]) // Re-run when state changes or loading/error state changes

  const handleGoToFunding = () => {
    setCurrentState('funding')
  }

  const handleGoBack = () => {
    setCurrentState('cost-adjustment')
  }

  const handleSubmit = () => {
    if (!assessmentData || !buildingUuid) return

    submitCustomAssessmentInput({
      buildingUuid,
      data: assessmentData,
    })
  }

  const handleRetry = () => {
    refetch()
  }

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          width: '100vw',
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <LoadingIndicator size={150} />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            Daten werden geladen
          </Typography>
        </Box>
      </Box>
    )
  }

  // Show error state if fetching fails
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          width: '100vw',
          backgroundColor: '#ffffff',
          padding: '24px',
        }}
      >
        <Box
          sx={{
            maxWidth: '500px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              color: '#740000',
            }}
          >
            Fehler beim Laden der Daten
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '14px',
              color: '#74777F',
            }}
          >
            {error.message}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: '100%',
            }}
          >
            <TextButton onClick={onComplete} height="56px" sx={{ flex: 1, paddingX: 0 }}>
              Überspringen
            </TextButton>
            <Button onClick={handleRetry} height="56px" sx={{ flex: 2, paddingX: 0 }}>
              Erneut versuchen
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  // First state: Cost adjustment
  if (currentState === 'cost-adjustment') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          width: '100%',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {/* Title at top left */}
        <Box
          sx={{
            flexShrink: 0,
            paddingX: '40px',
            paddingTop: '60px',
            paddingBottom: '16px',
            width: '100%',
            maxWidth: '880px',
            margin: '0 auto',
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: '20px',
            }}
          >
            Kosten anpassen{' '}
            <Box component="span" sx={{ color: '#74777F' }}>
              (optional)
            </Box>
          </Typography>
        </Box>

        {/* Main content area wrapper with overlays */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            position: 'relative',
          }}
        >
          {/* Top gradient overlay */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              left: 0,
              right: 0,
              height: '16px',
              background: 'linear-gradient(to bottom, #ffffff, transparent)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          {/* Main content area */}
          <Box
            sx={{
              paddingX: '40px',
              paddingTop: '16px',
              width: '100%',
              maxWidth: '880px',
              margin: '0 auto',
            }}
          >
            <CostAdjustmentContent
              formController={formController}
              assessmentData={assessmentData}
              setAssessmentData={setAssessmentData}
            />
          </Box>
          {/* Bottom gradient overlay */}
          {showBottomGradient && (
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                height: '8px',
                marginTop: '-8px',
                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent)',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
          )}
        </Box>

        {/* Bottom Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            padding: '16px 40px',
            flexShrink: 0,
            width: '100%',
            maxWidth: '880px',
            margin: '0 auto',
          }}
        >
          <Button
            onClick={handleGoToFunding}
            height="56px"
            sx={{ flex: 1 }}
            disabled={validateAssessmentData(assessmentData) !== AssessmentDataValidationError.NONE}
          >
            Weiter
          </Button>
        </Box>
      </Box>
    )
  }

  // Second state: Funding
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
      }}
    >
      {/* Title at top left */}
      <Box
        sx={{
          flexShrink: 0,
          paddingX: '40px',
          paddingTop: '60px',
          paddingBottom: '16px',
          width: '100%',
          maxWidth: '880px',
          margin: '0 auto',
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontWeight: 600,
            fontSize: '20px',
          }}
        >
          Förderungen{' '}
          <Box component="span" sx={{ color: '#74777F' }}>
            (optional)
          </Box>
        </Typography>
      </Box>

      {/* Main content area wrapper with overlays */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {/* Top gradient overlay */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            height: '16px',
            background: 'linear-gradient(to bottom, #ffffff, transparent)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        {/* Main content area */}
        <Box
          sx={{
            paddingX: '40px',
            paddingTop: '16px',
            width: '100%',
            maxWidth: '880px',
            margin: '0 auto',
          }}
        >
          <FundingContent assessmentData={assessmentData} setAssessmentData={setAssessmentData} />
        </Box>
        {/* Bottom gradient overlay */}
        {showBottomGradient && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              height: '8px',
              marginTop: '-8px',
              background: 'linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
      </Box>

      {/* Bottom Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          padding: '16px 40px',
          flexShrink: 0,
          width: '100%',
          maxWidth: '880px',
          margin: '0 auto',
        }}
      >
        <TextButton onClick={handleGoBack} height="56px" sx={{ flex: 1 }}>
          Zurück
        </TextButton>
        <Button onClick={handleSubmit} height="56px" sx={{ flex: 1 }} disabled={isSubmitting}>
          Fortfahren
        </Button>
      </Box>
    </Box>
  )
}

export default OptionalFields
